import { z } from 'zod';

export const MAX_EDITORIAL_NOTE_TEXT = 100_000 as const;
export const MAX_EDITORIAL_NOTES = 500 as const;

const editorialSha256Schema = z.string().regex(/^[a-f0-9]{64}$/, 'Hash de captura deve ser SHA-256 hexadecimal minúsculo');
const editorialSourcePathSchema = z.string().regex(
  /^detalhe\.(?:anotacoes|consideracaoFinal)\[\d+\]\.richText$/,
  'Caminho deve apontar para richText editorial documentado',
);
const editorialTemporalitySourcePathSchema = z.string().regex(
  /^detalhe\.anotacoes\[\d+\]\.dataAnotacao$/,
  'Caminho temporal deve apontar para dataAnotacao documentado',
);
const hasUnsafeControl = (value: string): boolean => Array.from(value).some((character) => {
  const code = character.codePointAt(0) ?? 0;
  return code <= 0x08 || code === 0x0B || code === 0x0C || code >= 0x0E && code <= 0x1F || code === 0x7F;
});

/**
 * Texto é sempre consumido como texto literal pelo integrador. Tags, scripts e
 * controles não são convertidos em narrativa nem enviados como HTML renderizável.
 */
export const safeEditorialTextSchema = z.string()
  .min(1)
  .max(MAX_EDITORIAL_NOTE_TEXT)
  .refine((value) => !hasUnsafeControl(value), 'Nota contém caractere de controle')
  .refine((value) => !/<\/?[A-Za-z][^>]*>|(?:java|vb)script\s*:/i.test(value), 'Nota não pode conter HTML ou script');
export type SafeEditorialText = z.infer<typeof safeEditorialTextSchema>;

export const editorialNoteTemporalitySchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('unproven') }),
  z.strictObject({
    status: z.literal('proven'),
    value: z.union([z.string().date(), z.string().datetime({ offset: true })]),
    sourcePath: editorialTemporalitySourcePathSchema,
  }),
]);
export type EditorialNoteTemporality = z.infer<typeof editorialNoteTemporalitySchema>;

export const editorialNoteSchema = z.strictObject({
  id: z.string().uuid(),
  pbId: z.string().regex(/^[A-Za-z0-9]{15}$/, 'pbId físico inválido'),
  clientId: z.string().uuid(),
  kind: z.literal('editorial_note_imported'),
  text: safeEditorialTextSchema,
  sourcePath: editorialSourcePathSchema,
  sourceCapture: z.strictObject({
    sourceInstance: z.string().min(1).max(200),
    sourceHash: editorialSha256Schema,
  }),
  temporality: editorialNoteTemporalitySchema,
});
export type EditorialNote = z.infer<typeof editorialNoteSchema>;

export const editorialNotesDataSchema = z.strictObject({
  version: z.literal(1),
  notes: z.array(editorialNoteSchema).max(MAX_EDITORIAL_NOTES),
}).superRefine((value, context) => {
  const ids = value.notes.map((note) => note.id);
  if (new Set(ids).size !== ids.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['notes'], message: 'Notas editoriais duplicam identidade estável' });
  }
  const paths = value.notes.map((note) => note.sourcePath);
  if (new Set(paths).size !== paths.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['notes'], message: 'Notas editoriais duplicam caminho de origem' });
  }
});
export type EditorialNotesData = z.infer<typeof editorialNotesDataSchema>;
